import { Controller, Resolver, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import cx from 'classnames';
import { trackEvent } from 'integrations/metrics';
import { EventNames } from 'integrations/metrics/events';
import { EventAction, EventObjectType } from 'integrations/metrics/types';
import { getBookableAppointments } from 'selectors/bookableAppointments';
import { getSelectedServices } from 'selectors/services';
import validator from 'validator';
import * as yup from 'yup';

import Button from 'components/Button';
import { House } from 'components/Icons/House';
import { Location } from 'components/Icons/Location';
import { InputField } from 'components/InputField/InputField';
import { Select } from 'components/Select';

import { useBusiness } from 'hooks/business/useBusiness';
import { usePreferences } from 'hooks/preferences/usePreferences';
import { useShowMore } from 'hooks/showMore/useShowMore';

import { states } from 'routes/book/consts/states';

import { TAppointmentHouseAddress } from 'types/appointment';

import s from './SelectLocation.module.css';

const statesOptions = [...Object.entries(states)].map(([value, label]) => ({
  value,
  label,
}));

type SelectLocationProps = {
  appointmentHouseAddress: TAppointmentHouseAddress;
  onSubmit: (appointMentHouseAddress: TAppointmentHouseAddress) => void;
};

export const SelectLocation = ({
  appointmentHouseAddress,
  onSubmit,
}: SelectLocationProps) => {
  const { addressLine1, addressLine2, city, state, zipCode, inHouse } =
    appointmentHouseAddress;

  const { isFixedLocation, doesHouseCalls } = usePreferences();
  const { address: fullAddress } = useBusiness();
  const bookableAppointments = useSelector(getBookableAppointments);
  const selectedServices = useSelector(getSelectedServices);

  const {
    text: address,
    isFullTextDisplayed,
    showFullText,
  } = useShowMore({ fullText: fullAddress });

  const locationSchema = yup
    .object({
      addressLine1: yup.string().required('Please enter the Address'),
      addressLine2: yup.string(),
      city: yup.string().required('Please enter the City'),
      state: yup.string().required('Please select the State'),
      zipCode: yup
        .string()
        .required('Please enter a Zip code')
        .test('zipcode', 'Please enter a valid Zip code', (value) => {
          return validator.isPostalCode(value, 'US') && value.length === 5;
        }),
    })
    .required();

  const resolver: Resolver<TAppointmentHouseAddress> = (
    values,
    context,
    options,
  ) => {
    const schema = values.inHouse ? locationSchema : yup.object();

    return yupResolver(schema)(values, context, options);
  };

  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
    watch,
  } = useForm<TAppointmentHouseAddress>({
    defaultValues: {
      addressLine1,
      addressLine2,
      city,
      state,
      zipCode,
      inHouse: inHouse || !isFixedLocation,
    },
    resolver,
    mode: 'onBlur',
  });

  const watchInHouse = watch('inHouse');

  const renderHouseCallText = () => {
    return isFixedLocation ? (
      <span>
        I prefer <br />a house call
      </span>
    ) : (
      <span>house call</span>
    );
  };

  const submitForm = (appointmentHouseAddress: TAppointmentHouseAddress) => {
    trackEvent(EventNames.NEXT_BUTTON_CLICKED_LOCATION, {
      action: EventAction.SELECT,
      productSection: 'Booking',
      objectType: EventObjectType.BUTTON,
      bundle_size: bookableAppointments.length,
      is_msmp_booking: bookableAppointments.length > 1,
      service_ids: selectedServices,
    });

    onSubmit(appointmentHouseAddress);
  };

  return (
    <form
      className={s.form}
      onSubmit={(event) => void handleSubmit(submitForm)(event)}
      noValidate
    >
      <Controller
        control={control}
        name="inHouse"
        render={({ field: { onChange, value } }) => (
          <div className={s.selectorContainer}>
            {isFixedLocation && address && (
              <label
                htmlFor="locationFixed"
                className={cx(s.location, {
                  [s.fullText]: isFullTextDisplayed,
                })}
              >
                <input
                  type="radio"
                  id="locationFixed"
                  name="location"
                  className={s.radio}
                  onChange={() => onChange(false)}
                  checked={value === false}
                />
                <Location className={s.icon} />
                <div className={s.addressWrap}>{address}</div>
                <div className={s.fullAddressWrap}>{fullAddress}</div>
                {!isFullTextDisplayed && (
                  <button
                    type="button"
                    className={s.moreButton}
                    onClick={showFullText}
                  >
                    Show More
                  </button>
                )}
              </label>
            )}
            {doesHouseCalls && (
              <label
                htmlFor="locationHome"
                className={cx(s.location, {
                  [s.fullText]: isFullTextDisplayed,
                })}
              >
                <input
                  type="radio"
                  id="locationHome"
                  name="location"
                  className={s.radio}
                  onChange={() => onChange(true)}
                  checked={value === true}
                />
                <House className={s.icon} />
                {renderHouseCallText()}
              </label>
            )}
          </div>
        )}
      />
      {(watchInHouse || !isFixedLocation) && (
        <div className={s.container}>
          <InputField
            id="addressLine1"
            label="Address (Street Number)"
            name="addressLine1"
            register={register('addressLine1')}
            error={errors.addressLine1?.message}
            required
          />

          <InputField
            id="addressLine2"
            label="Address 2 (Apt. / Suite No.)"
            name="addressLine2"
            register={register('addressLine2')}
            error={errors.addressLine2?.message}
            required
          />

          <InputField
            id="city"
            label="City"
            name="city"
            register={register('city')}
            error={errors.city?.message}
            required
          />

          <InputField
            id="zipCode"
            label="Zip code"
            name="zipCode"
            type="zipcode"
            register={register('zipCode')}
            error={errors.zipCode?.message}
            control={control}
            required
          />

          <Select
            id="state"
            label="State"
            name="state"
            options={statesOptions}
            control={control}
            error={errors.state?.message}
          />
        </div>
      )}

      <Button type="submit" className={s.button}>
        Next
      </Button>
    </form>
  );
};
